import { Location, Search } from 'history';
import queryString from 'query-string';

/**
 * Generate url with pagination's page index param
 * @param {Location} currentLocation
 * @param {number} newPage
 * @return {Location}
 */
function generatePaginationURLWithPage(currentLocation: Location, newPage: number): Location {
  if (newPage < 1) {
    return currentLocation;
  }
  const searchParams = queryString.parse(currentLocation.search);
  return {
    ...currentLocation,
    search: queryString.stringify({ ...searchParams, page: newPage })
  };
}

function generatePaginationURLWithPageV2(currentSearch: Search, newPage: number): Search {
  if (newPage < 1) {
    return currentSearch;
  }
  const searchParams = queryString.parse(currentSearch);
  return queryString.stringify({ ...searchParams, page: newPage });
}

/**
 * Generate url with pagination's page size param
 * @param {currentSearch} currentSearch
 * @param {number} newPage
 * @return {Location}
 */
function generateURLWithPerPage(currentSearch: Search, newSize: number): Search {
  if (newSize < 0) {
    return currentSearch;
  }
  const searchParams = queryString.parse(currentSearch);
  return queryString.stringify({ ...searchParams, take: newSize, page: 1 });
}

/**
 * Generate url with search param
 * @param {currentSearch} currentSearch
 * @param {string} searchText
 * @return {Search}
 */
function generateURLWithSearch(currentSearch: Search, searchString: string, searchText: string): Search {
  const searchParams = queryString.parse(currentSearch);
  return queryString.stringify({ ...searchParams, page: 1, [searchString]: searchText });
}

/**
 * Generate url with filter data
 * @param {currentSearch} currentSearch
 * @param {object} queryObject
 * @return {Search}
 */
function generateURLWithFilter(currentSearch: Search, queryObject: object): Search {
  const searchParams = queryString.parse(currentSearch);
  return queryString.stringify({ ...searchParams, ...queryObject }, { skipEmptyString: true });
}

/**
 * Get filter from url
 * @param {string} searchText
 * @return {Object[]}
 */
// eslint-disable-next-line @typescript-eslint/ban-types
function getFilterFromURL(searchText: string): {}[] {
  if (!searchText) return [];

  const urlQuery = queryString.parse(searchText);
  const result = [];

  // tslint:disable-next-line: forin
  for (const field in urlQuery) {
    const value = urlQuery[field];
    let id = '';

    if (field === 'status' || field === 'role') {
      id = field;
    } else if (/^q\[[a-zA-Z_]+\]$/.test(field)) {
      const pattern1 = /q\[|\]/g;
      const pattern2 = /_eq|_in|_lt|_lteq|_gt|_gteq|_cont$/;
      id = field.replace(pattern1, '').replace(pattern2, '');
    }

    if (id && value) {
      result.push({ id, value });
    }
  }
  return result;
}

function getTabIndex(tabs: string[]) {
  const query = queryString.parse(window.location.search);
  const defaultTabIndex = 0;
  if (!query.tab) return defaultTabIndex;

  const tabIndex = tabs.indexOf(query.tab as string);
  return tabIndex < 0 ? defaultTabIndex : tabIndex;
}

function getTabName(tabs: string[], tabIndex: number) {
  const tabName = tabs[tabIndex];
  if (!tabName) return '';

  return tabName || '';
}

export {
  generatePaginationURLWithPage,
  generatePaginationURLWithPageV2,
  generateURLWithPerPage,
  generateURLWithSearch,
  generateURLWithFilter,
  getFilterFromURL,
  getTabIndex,
  getTabName
};
